import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { PaymentProcessActions, PaymentProcessActionTypes } from "./../actions/payment-process.action";
import { Store, select } from "@ngrx/store";
import * as fromPayments from "./../reducers/index";
import { zip, Observable, from, Subscription, of } from "rxjs";
import { map, switchMap, exhaustMap, withLatestFrom, filter, catchError, tap } from "rxjs/operators";
import { PaymentDocument } from "./../../../components/bulk-payment/models/payment-document.model";
import { Update } from "@ngrx/entity";
import { PaymentRequest } from "./../../models/payment-request";
import { PaymentEngineService } from "./../../services/payment-engine.service";
import * as moment from "moment";
import { Customer } from "./../../../core/models/customer.model";
import { Company } from "./../../../core/models/company.model";
import { PaymentHeader } from "./../../models/payment-header";
import { PaymentDetail } from "./../../models/payment-detail";
import { PaymentConfigurationActions } from "./../actions/payment-configuration.action";
import { PayerBrokerRequest } from "./../../models/payer-broker-request";
import { PaymentMethod } from "./../../models/payment-method";
import { PaymentNetwork } from "./../../models/payment-network";
import { PaymentLayoutActions } from "./../actions/payment-layout.action";
import { Big } from "big.js";
import * as fromCore from "./../../../core/reducers/index";
import * as RouterActions from "@core/actions/router.action";

import { StoredData } from "../../../services/storedData.service";
import { IExternalAppData } from "./../../../core/models/externalAppData.model";
import { AppConfigurationService } from "../../../../cemexng2components/components/app-configuration/services/ipc-app-configuration.service";
import { AlertService, AlertType } from "@app/shared/alert";
import { TranslationService } from "@cemex-core/angular-localization-v7";

@Injectable()
export class PaymentProcessEffect {
  constructor(
    protected actions$: Actions,
    protected store$: Store<fromPayments.State>,
    protected storedData: StoredData,
    protected featureService: AppConfigurationService,
    private paymentEngineService: PaymentEngineService,
    private alertService: AlertService,
    private t: TranslationService
  ) {}

  startPaymentProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType<PaymentProcessActions.StartPaymentProcess>(PaymentProcessActionTypes.START_PAYMENT_PROCESS),
      switchMap(() =>
        from([
          new PaymentLayoutActions.ClearCustomers(),
          new PaymentLayoutActions.ClearCompanies(),
          new PaymentLayoutActions.ClearJobsites(),
          new PaymentConfigurationActions.ClearPaymentConfiguration(),
        ])
      )
    )
  );

  setDocumentAmountDue$ = createEffect(() =>
    this.actions$.pipe(
      ofType<PaymentProcessActions.SetDocumentsAmountDue>(PaymentProcessActionTypes.SET_DOCUMENTS_AMOUNT_DUE),
      switchMap(() => {
        return zip(
          this.store$.pipe(select(fromPayments.getPaymentConfiguration)),
          this.store$.pipe(select(fromPayments.getPaymentDocuments))
        ).pipe(
          filter(([paymentConfiguration, documents]) => paymentConfiguration !== undefined && documents !== undefined),
          map(([paymentConfiguration, documents]) => {
            const documentsWithDiscount = documents.filter(
              (document) => document.applyDiscount && document.discount > 0
            );
            const applyDiscount = paymentConfiguration.enabledDiscount && documentsWithDiscount.length > 0;

            const documentsUpdate: Update<PaymentDocument>[] = [];

            documents.forEach((document) => {
              let payment = document.paymentsBalance;

              if (applyDiscount) {
                payment -= document.discount;
              }

              document.payment = parseFloat(payment.toFixed(2));
              document.expectedPayment = parseFloat(payment.toFixed(2));

              // --- Logic from old regularPayment.component ---

              // TODO: validate if we need to varify this parameter *** this.getBooleanParameter(countryRes, this.COUNTRY, 'DUEDOCS');
              // TODO: verify if Bulk payment should be included

              const balance = new Big(document.paymentsBalance);
              if (document.applyDiscount) {
                const discount = new Big(document.discount);
                document.duePayment = balance.minus(discount);
              } else {
                document.duePayment = balance;
              }
              document.expectedDuePayment = new Big(document.duePayment.toString());
              // --- End of Logic from old regularPayment.component ---

              documentsUpdate.push({
                id: document.documentId,
                changes: {
                  payment: parseFloat(payment.toFixed(2)),
                  expectedPayment: parseFloat(payment.toFixed(2)),
                  duePayment: document.duePayment,
                  expectedDuePayment: document.expectedDuePayment,
                },
              });
            });
            return new PaymentProcessActions.UpdatePaymentDocuments(documentsUpdate);
          })
        );
      })
    )
  );

  createReferencePayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType<PaymentProcessActions.CreateReferencePayment>(PaymentProcessActionTypes.CREATE_REFERENCE_PAYMENT),
      withLatestFrom(
        this.store$.pipe(select(fromPayments.getPaymentHeader)),
        this.store$.pipe(select(fromPayments.getPaymentDetails)),
        this.store$.pipe(select(fromPayments.getPaymentExternalData))
      ),
      switchMap(([action, paymentHeader, paymentDetails, paymentExternalData]) => {
        // console.log("createReferencePaymentH:" + paymentHeader);
        // console.log("createReferencePaymentD:" + paymentDetails);
        // console.log("createReferencePaymentEX:" + paymentExternalData);
        let externalOrder = false;
        const request = {
          header: paymentHeader,
          detail: paymentDetails,
        };

        let isExternalOrder = false;
        if (paymentExternalData) {
          isExternalOrder = this.isExternalAppAndCompany(paymentHeader.companyCode, paymentExternalData);
        }

        return this.paymentEngineService.paymentEngine.createReference(request).pipe(
          switchMap((response) => {
            if (response.idTransaction) {
              const storeDataInfo = {
                UserAccount: this.storedData.getUserProfile().userAccount,
                Token: this.storedData.getRefreshToken(),
                ExternalOrder: isExternalOrder ? paymentExternalData.externalOrder : {},
                IsExternalAppAndCompany: isExternalOrder,
                PaymentExternalData: paymentExternalData,
              };
              const payerObj = this.paymentEngineService.paymentEngine.getHeaderPayerInformation(
                request,
                response,
                storeDataInfo
              );

              return from([
                new PaymentProcessActions.CreateReferencePaymentSuccess(response.idTransaction),
                new PaymentProcessActions.GetPayerInfomation(),
              ]);
            } else if (response.referenceNumber) {
              return from([
                new PaymentProcessActions.CreateReferencePaymentSuccess(response.referenceNumber),
                new PaymentProcessActions.GetPayerInfomation(),
              ]);
            }
          }),
          catchError((error) => {
            return of(new PaymentProcessActions.CreateReferencePaymentFailure());
          })
        );
      })
    )
  );

  createReferencePaymentFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<PaymentProcessActions.CreateReferencePaymentFailure>(
          PaymentProcessActionTypes.CREATE_REFERENCE_PAYMENT_FAILURE
        ),
        tap(() => {
          const ref = this.alertService.open(
            this.t.pt("views.error.failed_request_message"),
            AlertType.Error,
            false,
            null,
            this.t.pt("views.error.server_error_button"),
            () => {
              this.store$.dispatch(new PaymentLayoutActions.ExitPaymentsPage());
              this.alertService.close(ref);
            }
          );
        })
      ),
    { dispatch: false }
  );

  private isExternalAppAndCompany(companyCode: string, externaData: IExternalAppData): boolean {
    if (!companyCode) {
      return false;
    }

    if (externaData) {
      return false;
    }

    if (!externaData.sourceApp) {
      return false;
    }

    if (!this.featureService.isEnabled("payments.filter-external-app")) {
      return false;
    }

    const filterExternalAppFeature = this.featureService.getConfiguration("payments.filter-external-app");

    if (!filterExternalAppFeature) {
      return false;
    }

    const options = filterExternalAppFeature.options[externaData.sourceApp];

    if (!options) {
      return false;
    }

    return options.some((x) => x === companyCode);
  }

  getPayerInfomation$ = createEffect(() =>
    this.actions$.pipe(
      ofType<PaymentProcessActions.GetPayerInfomation>(PaymentProcessActionTypes.GET_PAYER_INFORMATION),
      withLatestFrom(
        this.store$.pipe(select(fromPayments.getSelectedCustomer)),
        this.store$.pipe(select(fromPayments.getSelectedCompany)),
        this.store$.pipe(select(fromPayments.getReferenceCode)),
        this.store$.pipe(select(fromPayments.getTotalToPay)),
        this.store$.pipe(select(fromPayments.getSelectedPaymentMethod)),
        this.store$.pipe(select(fromPayments.getSelectedNetworking)),
        this.store$.pipe(select(fromPayments.getPaymentExternalData)),
        this.store$.pipe(select(fromPayments.getPaymentHeader))
      ),
      // filter((([action, customer, company, referenceCode, totalToPay, paymentMethod, network, externalAppData]) => company !== undefined && company !== null),
      switchMap(
        ([
          action,
          customer,
          company,
          referenceCode,
          totalToPay,
          paymentMethod,
          network,
          externalAppData,
          paymentHeader,
        ]) => {
          let isExternalOrder = false;
          let haveSourceApp = false;
          let haveReturnUrl = false;
          let externalOrder = "";
          if (externalAppData) {
            isExternalOrder = this.isExternalAppAndCompany(company.companyCode, externalAppData);
            haveSourceApp = externalAppData.sourceApp ? true : false;
            haveReturnUrl = externalAppData.returnUrl ? true : false;
            externalOrder = externalAppData.externalOrder ? externalAppData.externalOrder : "";
          }

          const payerBroker: PayerBrokerRequest = {
            country: {
              countryCode: customer.countryCode,
            },
            customer: {
              customerCode: customer.customerCode,
              customerDesc: customer.customerDesc,
            },
            company: {
              companyCode: company.companyCode,
              applicationCode: haveSourceApp ? company.applicationCode : undefined,
              returnUrl: haveReturnUrl ? externalAppData.returnUrl : undefined,
            },
            referenceCode: referenceCode,
            amount: totalToPay,
            user: {
              userAccount: "", // add user
            },
            urlRedirect: "", // add url
            paymentType: {
              paymentTypeCode: paymentMethod.shortCode,
            },
            externalOrder: externalOrder,
            paymentHeader: paymentHeader,
          };
          if (isExternalOrder && externalOrder != "") {
            // Save external order relation on bd
            this.paymentEngineService.paymentEngine
              .saveExternalOrderRelation(
                externalOrder,
                payerBroker.referenceCode,
                "PSE01",
                payerBroker.company.companyCode,
                payerBroker.customer.customerCode,
                payerBroker.company.companyCode
              )
              .subscribe(
                (res) => {
                  console.log(res);
                },
                (error) => {
                  console.log(error);
                }
              );
          }
          return this.paymentEngineService.paymentEngine
            .getPayerBroker(payerBroker, network.bankCode)
            .pipe(map((response) => new PaymentProcessActions.GetPayerInfomationSuccess(response)));
        }
      )
    )
  );

  initPaymentEngineService$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<PaymentProcessActions.InitPaymentEngineService>(PaymentProcessActionTypes.INIT_PAYMENT_ENGINE_SERVICE),
        withLatestFrom(
          this.store$.pipe(select(fromPayments.getSelectedCustomer)),
          this.store$.pipe(select(fromPayments.getSelectedCompany))
        ),
        filter(([action, customer, company]) => customer !== undefined && company !== undefined && company !== null),
        map(([action, customer, company]) => {
          this.paymentEngineService.initPaymentEngine(customer, company);
        })
      ),
    { dispatch: false }
  );

  callToNetworking$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<PaymentProcessActions.CallToNetworking>(PaymentProcessActionTypes.CALL_TO_NETWORKING),
        withLatestFrom(
          this.store$.pipe(select(fromPayments.getTotalToPay)),
          this.store$.pipe(select(fromPayments.getReferenceCode)),
          this.store$.pipe(select(fromPayments.getPayerInformation))
        ),
        filter(
          ([action, totalToPay, referenceCode, payerInformation]) =>
            totalToPay !== undefined && referenceCode !== undefined && payerInformation !== undefined
        ),
        map(([action, totalToPay, referenceCode, payerInformation]) => {
          this.paymentEngineService.paymentEngine.callToNetworking(totalToPay, referenceCode, payerInformation);
        })
      ),
    { dispatch: false }
  );

}
